import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { connect } from 'react-redux';
import { Panel } from 'react-bootstrap';
import { faSpinner, faDownload, faUndo } from '@fortawesome/pro-solid-svg-icons';

import PageHeader from 'components/project/page_header';
import SelectFilter from 'components/search_sidebar/select_filter';
import SearchPage from 'components/search_page';
import FilterTopbar from 'components/search_page/filter_topbar';
import FilterRow from 'components/search_page/filter_row';

import { get } from 'helpers/api';
import ActivityTable from './activity_table';
import EmptyState from 'shared/empty_state';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import Pagination from 'shared/pagination';
import axios from 'axios';

const ACTIVITIES_PER_PAGE = 50;

const ACTIVITY_TYPES = [
  { value: '', label: 'All User Activities' },
  { value: 'VaultActivities::DocumentView', label: 'Downloaded Document' },
  { value: 'VaultActivities::CaSigned', label: 'Signed CA' },
];

const SALE_OR_LEASE = [
  { value: '', label: 'Sale & Lease' },
  { value: 'sale', label: 'Sale' },
  { value: 'lease', label: 'Lease' }
];

const startingFilters = {
  searchText: '',
  activityType: ACTIVITY_TYPES[0].value,
  saleOrLease: SALE_OR_LEASE[0].value
};

const CONTENT_TO_SHOW = {
  LOADING: 'loading',
  EMPTY_STATE: 'empty_state',
  TABLE: 'table'
};

export function SharingActivityTab({
  activityCsvVaultsPath,
  features,
  vaultActivitiesPropertyPath,
  vaultActivitiesSvgPath
}) {
  const [activities, setActivities] = useState([]);
  const [filters, setFilters] = useState(startingFilters);
  const [loading, setLoading] = useState(true);
  const [vaultIds, setVaultIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalActivities, setTotalActivities] = useState(0);
  const requestTimeoutId = useRef();

  useEffect(() => {
    if (!features.leadManagement) return;

    const abortController = new AbortController();
    fetchActivities(filters, currentPage, abortController);

    return () => abortController.abort();
  }, []);

  function fetchActivities(filtersForSearch = filters, offset = currentPage, abortController = null) {
    setLoading(true);
    const params = {
      name: filtersForSearch.searchText,
      type: filtersForSearch.activityType,
      saleOrLease: filtersForSearch.saleOrLease,
      offset
    };

    const data = { params: params };
    if (abortController) data.signal = abortController.signal;

    get(vaultActivitiesPropertyPath, data).then((data) => {
      setLoading(false);
      setVaultIds(data.vaultIds);
      setActivities(data.activities);
      setTotalActivities(data.totalActivities);
    }).catch((error) => {
      // eslint-disable-next-line no-console
      if (!axios.isCancel(error)) console.log(error);
    });
  }

  function handleFilterChange(filter, shouldDelay = false) {
    const updatedFilters = { ...filters, ...filter };
    setFilters(updatedFilters);
    setCurrentPage(1);

    clearTimeout(requestTimeoutId.current);
    if (shouldDelay) {
      requestTimeoutId.current = setTimeout(() => fetchActivities(updatedFilters, 1), 300);
    } else {
      fetchActivities(updatedFilters, 1);
    }
  }

  function updateCurrentPage(newPageNumber) {
    setCurrentPage(newPageNumber);
    fetchActivities(filters, newPageNumber);
  }

  function filtersAreEmpty() {
    Object.keys(filters).every(filterKey => filters[filterKey] === startingFilters[filterKey]);
  }

  const clearFilters = () => handleFilterChange(startingFilters);
  const handleSearchTextChange = e => handleFilterChange({ searchText: e.target.value || '' }, true);
  const handleActivityTypeChange = filter => handleFilterChange({ activityType: filter.value });
  const handleSaleOrLeaseChange = filter => handleFilterChange({ saleOrLease: filter.value });
  const currentActivityType = () => ACTIVITY_TYPES.find(activity => activity.value === filters.activityType);
  const currentSaleOrLease = () => SALE_OR_LEASE.find(saleOrLease => saleOrLease.value === filters.saleOrLease);

  function renderFilterTopbar() {
    return (
      <SearchPage>
        <FilterTopbar className="w-100 p-0 border-0 mb-3">
          <FilterRow
            name="filters"
            searchPlaceholder="Search..."
            searchText={filters.searchText}
            onSearchChange={handleSearchTextChange}
          >
            <div className="left-buffer-md">
              <SelectFilter
                defaultValue={ACTIVITY_TYPES[0]}
                name="activity-type-filter"
                options={ACTIVITY_TYPES}
                value={currentActivityType()}
                onChange={handleActivityTypeChange}
              />
            </div>
            {vaultIds.length > 1 && (
              <div className="left-buffer-md">
                <SelectFilter
                  defaultValue={SALE_OR_LEASE[0]}
                  name="sale-or-lease-filter"
                  options={SALE_OR_LEASE}
                  value={currentSaleOrLease()}
                  onChange={handleSaleOrLeaseChange}
                />
              </div>
            )}
          </FilterRow>
        </FilterTopbar>
        <a className="text-nowrap d-flex align-items-center" href={activityCsvVaultsPath}>
          <span>
            <FontAwesomeIcon className="mr-2" icon={faDownload}/>
            Download CSV
          </span>
        </a>
      </SearchPage>
    );
  }

  if (!loading && activities.length === 0 && filtersAreEmpty()) {
    return (
      <div>
        <Panel className="dashed-border">
          <Panel.Heading>
            <div className="info-well text-center top-buffer-sm bottom-buffer-sm">
              <svg className="bo-icon bo-icon-activity bo-icon-lg">
                <use xlinkHref={vaultActivitiesSvgPath}/>
              </svg>
              <h3>There has been no user activity for this property yet!</h3>
            </div>
          </Panel.Heading>
        </Panel>
      </div>
    );
  }

  const startingRange = ((currentPage - 1) * ACTIVITIES_PER_PAGE) + 1;
  const endingRange = Math.min(currentPage * ACTIVITIES_PER_PAGE, totalActivities);

  let contentToShow = CONTENT_TO_SHOW.TABLE;
  if (loading) {
    contentToShow = CONTENT_TO_SHOW.LOADING;
  } else if (activities.length === 0) {
    contentToShow = CONTENT_TO_SHOW.EMPTY_STATE;
  }

  return (
    <div>
      <PageHeader title="Web Activity"/>
      {renderFilterTopbar()}
      {contentToShow === CONTENT_TO_SHOW.LOADING && (
        <div className="text-center my-5" name="loading-spinner">
          <FontAwesomeIcon icon={faSpinner} size="2x" spin/>
        </div>
      )}
      {contentToShow === CONTENT_TO_SHOW.EMPTY_STATE && (
        <EmptyState
          bodyText="We were unable to find any Web Activity matching these filters"
          buttonIcon={faUndo}
          buttonText="Reset Filters"
          header="Unable to find Web Activity"
          icon={faExclamationCircle}
          onClick={clearFilters}
        />
      )}
      {contentToShow === CONTENT_TO_SHOW.TABLE && (
        <React.Fragment>
          <div className="text-muted text-right mb-2">
            Displaying {startingRange} - {endingRange} of {totalActivities} Web Activity
          </div>
          <ActivityTable activities={activities} vaultIds={vaultIds}/>
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalActivities / ACTIVITIES_PER_PAGE)}
            onChange={updateCurrentPage}
          />
        </React.Fragment>
      )}
    </div>
  );
}

const mapStateToProps = state => ({
  features: state.features,
});

SharingActivityTab.propTypes = {
  activityCsvVaultsPath: PropTypes.string.isRequired,
  features: PropTypes.shape({
    projectTasksActivities: PropTypes.bool
  }).isRequired,
  vaultActivitiesPropertyPath: PropTypes.string.isRequired,
  vaultActivitiesSvgPath: PropTypes.string.isRequired
};

export default connect(mapStateToProps)(SharingActivityTab);
